<template>
  <div
    :class="[
      `group/input relative flex flex-col items-center justify-center gap-2 overflow-hidden rounded-lg border text-center text-sm font-medium text-muted-foreground transition`,
      aspectRatio === 'opengraph'
        ? `aspect-[1.91/1] w-full max-w-sm`
        : `size-48`,
      `outline-2 -outline-offset-1 outline-blue-500 has-[:focus-visible]:outline`,
      isHovered && !displayUrl ? `bg-card` : `bg-input`,
      isDisabled && `opacity-50`,
      fieldContext?.validationStatus.value === 'error'
        ? `border-red-300 outline-red-500 dark:border-red-800`
        : `border-border outline-blue-500`,
    ]"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <template v-if="displayUrl">
      <img
        :key="displayUrl"
        :src="displayUrl"
        class="size-full object-cover transition"
        :class="[pendingImage && `brightness-[.25]`]"
      />

      <Transition enter-from-class="opacity-0" leave-to-class="opacity-0">
        <div
          v-if="pendingImage?.status === 'uploading'"
          class="absolute inset-0 flex flex-col items-center justify-center gap-2 transition"
        >
          <CircularProgress :value="pendingImage.progress" class="size-8" />

          <span class="text-xs font-medium text-white">
            <Value.Percentage :value="pendingImage.progress" />
          </span>
        </div>

        <div
          v-else-if="pendingImage?.status === 'failed'"
          class="absolute inset-0 flex flex-col items-center justify-center gap-2 transition"
        >
          <ExclamationTriangleIcon class="size-6 text-white" />

          <span class="text-xs font-medium text-white">Upload failed</span>

          <div class="flex gap-2">
            <button
              type="button"
              class="flex items-center gap-1 rounded bg-white/20 px-2 py-1 text-xs font-medium text-white hover:bg-white/30"
              @click="retryUpload"
            >
              <ArrowPathIcon class="size-3" />
              Retry
            </button>

            <button
              type="button"
              class="flex items-center gap-1 rounded bg-white/20 px-2 py-1 text-xs font-medium text-white hover:bg-white/30"
              @click="cancelUpload"
            >
              <XMarkIcon class="size-3" />
              Cancel
            </button>
          </div>
        </div>
      </Transition>
    </template>
    <template v-else>
      <PlusIcon class="size-6" />

      <span>Add image</span>
    </template>

    <Menu.Root v-if="!pendingImage" :open="open" @update:open="onOpenChange">
      <Menu.Trigger>
        <button
          :id="fieldContext?.inputId"
          ref="trigger"
          type="button"
          :disabled="isDisabled"
          class="absolute inset-0 flex items-center justify-center outline-none"
          :aria-invalid="fieldContext?.validationStatus.value === 'error'"
          :aria-describedby="ariaDescribedBy"
          @focus="onFocus"
          @blur="onBlur"
        >
          <Transition enter-from-class="opacity-0" leave-to-class="opacity-0">
            <div
              v-if="isHovered && displayUrl"
              class="absolute inset-0 flex items-center justify-center transition"
            >
              <div class="absolute inset-0 bg-background opacity-50"></div>

              <PencilIcon class="z-10 size-6 text-foreground" />
            </div>
          </Transition>
        </button>
      </Menu.Trigger>

      <Menu.Container align="center">
        <Menu.Item icon="pencil" label="Change image" @click="openFilePicker" />
        <Menu.Item
          icon="trash"
          label="Remove image"
          variant="danger"
          @click="avatarUrl = ''"
        />
      </Menu.Container>
    </Menu.Root>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef } from "vue";

import CircularProgress from "@/design_system/CircularProgress.vue";
import Menu from "@/design_system/Menu";
import Value from "@/design_system/Value";
import ArrowPathIcon from "@/icons/heroicons/arrow-path-micro.svg";
import ExclamationTriangleIcon from "@/icons/heroicons/exclamation-triangle-micro.svg";
import PencilIcon from "@/icons/heroicons/pencil-micro.svg";
import PlusIcon from "@/icons/heroicons/plus-micro.svg";
import XMarkIcon from "@/icons/heroicons/x-mark-micro.svg";
import { useUploadImageMutation } from "@/lib/api/queries/images/list";
import { useFilePicker } from "@/lib/dropzone";

import { injectFieldContext } from "./context";

const avatarUrl = defineModel<string>({ default: "" });

const props = defineProps<{
  aspectRatio?: "square" | "opengraph";
  disabled?: boolean;
  onFocus?: (event: FocusEvent) => void;
  onBlur?: (event: FocusEvent) => void;
}>();

const aspectRatio = computed(() => props.aspectRatio ?? "square");

const trigger = useTemplateRef("trigger");
const fieldContext = injectFieldContext(null);

const isDisabled = computed(() => {
  return fieldContext?.disabled.value || props.disabled;
});

const ariaDescribedBy = computed(() => {
  if (!fieldContext) {
    return undefined;
  }

  const ids: string[] = [
    fieldContext.descriptionId,
    fieldContext.hintId,
    fieldContext.validationMessageId,
  ];

  return ids.join(" ");
});

const open = ref(false);
const isHovered = ref(false);

const uploadMutation = useUploadImageMutation({ keepFailedImages: true });
const pendingImage = computed(() => uploadMutation.pending.value.at(0));

const displayUrl = computed(() => {
  return pendingImage.value?.previewUrl ?? avatarUrl.value;
});

const uploadFile = (file: File) => {
  uploadMutation.mutateAsync(file).then((image) => {
    avatarUrl.value = image.image;
  });
};

const retryUpload = () => {
  const pending = pendingImage.value;
  if (pending) {
    uploadFile(pending.file);
  }
};

const cancelUpload = () => {
  const pending = pendingImage.value;
  if (pending) {
    uploadMutation.dismissPendingImage(pending.file);
  }
};

const { open: openFilePicker } = useFilePicker({
  accept: ["image/*"],
  multiple: false,
  onDrop(files) {
    if (!files) {
      return;
    }

    const file = files[0];
    uploadFile(file);
  },
});

const onOpenChange = (next: boolean) => {
  if (next && !avatarUrl.value) {
    openFilePicker();
    return;
  }

  open.value = next;
};

defineExpose({
  input: computed(() => trigger.value),
});
</script>
